import React from 'react';
import { useSelector } from 'react-redux';
import ViewLabel from '../generic-component/ViewLabel.js'; 
import NewInputGeneric from '../generic-component/NewInputGeneric';
import NewRelationGeneric from '../generic-component/NewRelationGeneric';
import NewTextGeneric from '../generic-component/NewTextGeneric';
import EditYearGeneric from '../generic-component/EditYearGeneric';
import NewDateGeneric from '../generic-component/NewDateGeneric';
import NewCatalogRelationGeneric from '../generic-component/NewCatalogRelationGeneric';
import NewCatalogLocationGeneric from '../generic-component/NewCatalogLocationGeneric';
import NewCatalogRelationProblems from '../generic-component/NewCatalogRelationProblems';
import NewCatalogRelationMultimedias from '../generic-component/NewCatalogRelationMultimedias';
import NewCatalogRelationProblemsDetails from '../generic-component/NewCatalogRelationProblemsDetails';
import NewRelationGenericDescription from '../generic-component/NewRelationGenericDescription.js';
import { resolveImportStageFromState } from '../../app/helpers/importStageHelper';
import { isNewEditPreManagerFlag } from '../../app/helpers/moduleConfigPreNewEditHelper';

const PRE_ALWAYS_FIELDS = new Set(['dri', 'dri_registered_at']);

const isFormRowVisible = (item, visibilityFlag) => {
    if (visibilityFlag === 'new_edit_premanager') {
        return item?.type === 'section'
            || isNewEditPreManagerFlag(item)
            || PRE_ALWAYS_FIELDS.has(item?.field);
    }
    return !!item?.show_new;
};

const DynamicFormEngine = ({
    list,
    values,
    errors,
    setValues,
    handleChange,
    handleChangeLocation,
    handleChangeMulti,
    handleChangeCatalogPhase,
    handleChangeCatalogCiiu,
    handleChangeCatalogSection,
    handleChangeCatalogInstitution,
    getMethodData,
    getSelectedProblemsOriginalCatalogs, 
    listProblems,
    listMultimedias,
    relationKeys = {
        problems: "ModProblem_To_Technology",
        multimedia: "ModuleFile_To_Technology"
    },
    isEditMode = false,
    visibilityFlag
}) => {
    const userRole = useSelector((state) => state.userRole);
    const resolvedVisibilityFlag = visibilityFlag
        || (resolveImportStageFromState({ userRole }) === 'pre' ? 'new_edit_premanager' : 'show_new');

    if (!list || list.length === 0) return null;

    return list.map((techSecondary) => {
        if (!isFormRowVisible(techSecondary, resolvedVisibilityFlag)) return null;
        if (techSecondary.type === 'section' || techSecondary.component_new === 'ViewSectionLabel') return null;

        const varField = techSecondary.field;
        const varRelation = techSecondary.key_relation;
        const { comboData, level } = getMethodData(techSecondary.data_name);
        const occupation = resolvedVisibilityFlag === 'new_edit_premanager'
            ? (techSecondary.occupation_pre ?? techSecondary.occupation_new)
            : techSecondary.occupation_new;

        const commonProps = {
            values,
            errors,
            field: varField,
            moduleText: techSecondary.module_text,
        };

        const inputProps = {
            ...commonProps,
            handleChange,
            placeholderVal: techSecondary.is_placeholder,
            occupation,
        };

        switch (techSecondary.component_new) {
            case "ViewGroupLabel":
                return (
                    <ViewLabel
                        key={techSecondary.id}
                        {...commonProps}
                        values2={varRelation}
                        max_length={techSecondary.max_length}
                        occupation={techSecondary.occupation_view}
                    />
                );

            case "NewInputGeneric":
                return <NewInputGeneric key={techSecondary.id} {...inputProps} max_length={techSecondary.max_length} />;

            case "NewRelationGeneric":
                return <NewRelationGeneric key={techSecondary.id} {...inputProps} comboData={comboData} />;

            case "NewRelationGenericDescription":
                return <NewRelationGenericDescription key={techSecondary.id} {...inputProps} comboData={comboData} />;

            case "NewTextGeneric":
                return <NewTextGeneric key={techSecondary.id} {...inputProps} max_length={techSecondary.max_length} />;

            case "NewYearGeneric":
                return <EditYearGeneric key={techSecondary.id} {...inputProps} />;

            case "NewDateGeneric":
                return <NewDateGeneric key={techSecondary.id} {...inputProps} />;

            case "NewCatalogRelationGeneric":
                let handlerCatalog = handleChangeCatalogPhase;
                if (techSecondary.comparable_relation_condition === "CatalogCiius") {
                    handlerCatalog = handleChangeCatalogCiiu;
                } else if (techSecondary.comparable_relation_condition === "CatalogSections") {
                    handlerCatalog = handleChangeCatalogSection;
                } else if (techSecondary.comparable_relation_condition === "CatalogInstitutions") {
                    handlerCatalog = handleChangeCatalogInstitution;
                }
                if (level > 1 && (!comboData || comboData.length === 0)) {
                    return null;
                }
                return (
                    <NewCatalogRelationGeneric
                        key={techSecondary.id}
                        {...inputProps}
                        handleChange={handlerCatalog}
                        max_length={techSecondary.max_length}
                        comboData={comboData}
                        level={level}
                    />
                );

            case "NewCatalogLocationGeneric":
                return <NewCatalogLocationGeneric key={techSecondary.id} {...inputProps} handleChange={handleChangeLocation} comboData={comboData} />;

            case "NewCatalogRelationProblems":
                let problemsData = [];
                const problemKey = relationKeys.problems;
                if (values?.[problemKey]?.length > 0) {
                    problemsData = getSelectedProblemsOriginalCatalogs(values[problemKey], listProblems);
                }
                return (
                    <NewCatalogRelationProblems
                        key={techSecondary.id}
                        {...inputProps}
                        handleChange={handleChangeMulti}
                        comboData={comboData}
                        defaultData={problemsData}
                    />
                );

            case "NewCatalogRelationTemporal":
                let temporalData = [];
                if (isEditMode) {
                    if (varField === 'general_adaptation') temporalData = values.general_adaptation_list;
                    else if (varField === 'structural_adaptation') temporalData = values.structural_adaptation_list;
                    else if (varField === 'specific_adaptation') temporalData = values.specific_adaptation_list;
                }
                return (
                    <NewCatalogRelationProblems
                        key={techSecondary.id}
                        {...inputProps}
                        handleChange={handleChangeMulti}
                        comboData={comboData}
                        defaultData={temporalData}
                    />
                );

            case "NewCatalogRelationMultimedias":
                const multimediaKey = relationKeys.multimedia;
                return (
                    <NewCatalogRelationMultimedias
                        key={techSecondary.id}
                        tableRecords={values?.[multimediaKey] || []}
                        setValues={setValues}
                        comboData={listMultimedias}
                        field={varField}
                        moduleText={techSecondary.module_text}
                        occupation={occupation}
                        keyRelation={multimediaKey}
                    />
                );

            case "NewCatalogRelationProblemsList":
                const problemKeyList = relationKeys.problems; 
                return (
                    <NewCatalogRelationProblemsDetails
                        key={techSecondary.id}
                        tableRecords={values?.[problemKeyList] || []}
                        setValues={setValues}
                        comboData={listProblems}
                        field={varField}
                        moduleText={techSecondary.module_text}
                        occupation={occupation}
                    />
                );

            default:
                return <div key={techSecondary.id}></div>;
        }
    });
};

export default DynamicFormEngine;
